import React from 'react';
import { cn } from './cn';
import { Icon } from './base/Icon';

/**
 * BulkActionBar — โผล่ขึ้นเมื่อเลือกหลายแถว
 *
 * กติกาที่ผูกกับ FR-ADM-13 และประเด็นข้อ ช ในเอกสารแผน:
 * ปุ่มที่ทำแล้วย้อนไม่ได้ต้องส่ง tone='danger' และ requiresConfirm
 * และแถบนี้ต้องบอก "จำนวนที่เลือก" เสมอ ไม่ใช่แค่คำว่า "เลือกอยู่"
 */
export interface BulkAction {
  id: string;
  label: string;
  icon: string;
  tone?: 'default' | 'primary' | 'danger';
  disabled?: boolean;
  /** ข้อความอธิบายผลที่จะเกิด แสดงใน ConfirmDialog */
  consequence?: string;
  onRun: (selectedKeys: string[]) => void;
}

export interface BulkActionBarProps {
  selectedKeys: string[];
  actions: BulkAction[];
  onClear: () => void;
  /** จำนวนทั้งหมดที่ตรงตัวกรอง — ใช้กับปุ่ม "เลือกทั้งหมดที่ตรงตัวกรอง" */
  totalMatching?: number;
  onSelectAllMatching?: () => void;
  className?: string;
}

const toneClass: Record<NonNullable<BulkAction['tone']>, string> = {
  default: 'bg-[var(--surface-0)] text-[var(--ink-900)] border-[var(--line-300)] hover:bg-[var(--blue-050)]',
  primary: 'bg-[var(--blue-700)] text-white border-transparent hover:bg-[var(--blue-800)]',
  danger: 'bg-[var(--error-solid)] text-white border-transparent hover:bg-[var(--error-text)]',
};

export const BulkActionBar: React.FC<BulkActionBarProps> = ({
  selectedKeys, actions, onClear, totalMatching, onSelectAllMatching, className,
}) => {
  const count = selectedKeys.length;
  if (count === 0) return null;

  return (
    <div
      role="region"
      aria-label="การทำงานกับรายการที่เลือก"
      className={cn(
        'sticky bottom-[var(--space-4)] z-[var(--z-bulkbar)] mx-auto flex w-fit max-w-full flex-wrap items-center gap-[var(--space-3)] rounded-[var(--radius-lg)] border border-[var(--blue-200)] bg-[var(--surface-0)] px-[var(--space-4)] py-[var(--space-25)] font-[family-name:var(--font-thai)] shadow-[var(--elevation-toast)]',
        className,
      )}
    >
      <span className="flex items-center gap-[var(--space-2)] text-[length:var(--text-form)] text-[var(--ink-900)]">
        <Icon name="check_box" size={18} filled className="text-[var(--blue-700)]" />
        เลือกอยู่ <span className="bo-num font-[var(--weight-semibold)]">{count.toLocaleString('th-TH')}</span> รายการ
      </span>

      {onSelectAllMatching && totalMatching !== undefined && totalMatching > count && (
        <button
          type="button"
          onClick={onSelectAllMatching}
          className="min-h-[var(--hit-table)] rounded-[var(--radius-sm)] px-[var(--space-2)] text-[length:var(--text-form)] font-[var(--weight-medium)] text-[var(--blue-700)] underline underline-offset-2 hover:text-[var(--blue-800)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)] focus-visible:ring-offset-2"
        >
          เลือกทั้งหมดที่ตรงตัวกรอง ({totalMatching.toLocaleString('th-TH')})
        </button>
      )}

      <span aria-hidden className="h-5 w-px bg-[var(--line-200)]" />

      <div className="flex flex-wrap items-center gap-[var(--space-2)]">
        {actions.map((a) => (
          <button
            key={a.id}
            type="button"
            disabled={a.disabled}
            onClick={() => a.onRun(selectedKeys)}
            title={a.consequence}
            className={cn(
              'inline-flex min-h-[var(--hit-control)] items-center gap-[var(--space-15)] rounded-[var(--radius-sm)] border px-[var(--space-3)] text-[length:var(--text-form)] font-[var(--weight-medium)] disabled:cursor-not-allowed disabled:opacity-50 outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)] focus-visible:ring-offset-2',
              toneClass[a.tone ?? 'default'],
            )}
          >
            <Icon name={a.icon} size={16} />
            {a.label}
          </button>
        ))}
      </div>

      <button
        type="button"
        onClick={onClear}
        aria-label="ยกเลิกการเลือกทั้งหมด"
        className="inline-flex min-h-[var(--hit-icon-sm)] min-w-[var(--hit-icon-sm)] items-center justify-center rounded-[var(--radius-sm)] text-[var(--ink-600)] hover:bg-[var(--surface-100)] hover:text-[var(--ink-900)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)] focus-visible:ring-offset-2"
      >
        <Icon name="close" size={18} />
      </button>
    </div>
  );
};
